import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import userService from "../../../services/user/user.service";
import { DatePicker, Image } from "antd";
import dayjs from "dayjs";
import { PermissionType, User, UserUpdateDTO } from "../../../types/user.types";
import { DEFAULT_USER_CREATE_DTO, mapUserToUpdateDTO } from "../../../constants/userConstants";
import Swal from 'sweetalert2';
import FileUpload from '../../../components/shared/components/upload/FileUpload';
import { FileDto, FileType, DocumentType, DOCUMENT_TYPE_LABELS, getDocumentTypeOptions } from '../../../types/file.types';
import FileService from '../../../services/file/file.service';
import { getFileUrl } from '../../../utils/file.utils';
import authService from '../../../services/auth/auth.service';
import { logout } from "../../../store/slices/auth.slice";
import { AppDispatch } from "../../../store/store";
import { Modal, Input, Select } from 'antd';
import { message } from 'antd';
import { handleApiError } from '../../../utils/errorHandler';
import { useTranslation } from 'react-i18next';
import FooterSection from "../../shared/components/footerSection";
import i18n from "../../../i18n/config";
import datePickerTr from 'antd/lib/date-picker/locale/tr_TR';
import datePickerEn from 'antd/lib/date-picker/locale/en_US';
import BloodTypeSelect from '../../shared/components/BloodTypeSelect';
import ChronicDiseasesInput from '../../shared/components/ChronicDiseasesInput';
import { useUserAssets } from '../../../hooks/useAssets';
import { Asset, AssetStatus } from '../../../types/asset.types';
import { Table, Tag } from 'antd';
import { RolesEnum } from "../../../types/route.types";
type PasswordField =
  | "oldPassword"
  | "newPassword"
  | "confirmPassword"
  | "currentPassword";

const Profile = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { getUserById, updateUser, updateUserSuperAdmin } = userService;
  const [userData, setUserData] = useState<User | null>(null);
  const [formState, setFormState] = useState<UserUpdateDTO>(DEFAULT_USER_CREATE_DTO);
  const [loading, setLoading] = useState(false);
  const fileService = FileService.getInstance();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Şifre değiştirme state'leri
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const routes = all_routes;
  const handleInputChange = (field: keyof (UserUpdateDTO), value: any) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const fetchUser = async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const userData = await getUserById(user.id);
      setUserData(userData);
      setFormState(mapUserToUpdateDTO(userData));
      setLoading(false);
    } catch (error: any) {
      handleApiError(error, t);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [user?.id]);

  const handleProfilePhotoUpload = async (file: File | null) => {
    setSelectedFile(file);
  };

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user?.id) return;
    
    setLoading(true);
    try {
      let profilePictureUrl = userData?.profilePicture || null;

      // Profil fotoğrafı yükleme
      if (selectedFile) {
        //console.log(selectedFile);

        try {
          const uploadResponse = await fileService.uploadProfilePictureFile(selectedFile);
          
          if (uploadResponse) {
            profilePictureUrl = uploadResponse.filePath.path;
          }
          
        } catch (error) {
          handleApiError(error, t);
        }
      }

      // Kullanıcı bilgilerini güncelleme
      const updatedFormState = {
        ...formState,
        profilePicture: profilePictureUrl,
        birthDate: formState.birthDate 
          ? dayjs(formState.birthDate).format('YYYY-MM-DD') : null,
      };
      const formStateUpdate = {
        firstName: formState.firstName,
        lastName: formState.lastName,
        phone: formState.phone,
        emergencyContact: formState.emergencyContact,
        birthDate: formState.birthDate ? dayjs(formState.birthDate).format('YYYY-MM-DD') : null,
        maritalStatus: formState.maritalStatus,
        educationLevel: formState.educationLevel,
        city: formState.city,
        bloodType: formState.bloodType,
        chronicDiseases: formState.chronicDiseases,
      }
      
      await updateUser(user.id, formStateUpdate);

      Swal.fire({
        title: t('profilePage.success.success'),
        text: t('profilePage.success.profileUpdated'),
        icon: 'success',
        confirmButtonText: t('common.buttons.ok')
      });

      // Güncellenen kullanıcı bilgilerini getir
      await fetchUser();
    } catch (error) {
      handleApiError(error, t);
    } finally {
      setSelectedFile(null);
      setLoading(false);
    }
  };


  // Şifre değiştirme fonksiyonu
  const handleChangePassword = async () => {
    // Validasyon
    if (!currentPassword) {
      Swal.fire({
        title: t('profilePage.error.error'),
        text: t('profilePage.error.currentPasswordRequired'),
        icon: 'error',
        confirmButtonText: t('common.buttons.ok')
      });
      return;
    }

    if (!newPassword) {
      Swal.fire({
        title: t('profilePage.error.error'),
        text: t('profilePage.error.newPasswordRequired'),
        icon: 'error',
        confirmButtonText: t('common.buttons.ok')
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      Swal.fire({
        title: t('profilePage.error.error'),
        text: t('profilePage.error.newPasswordNotMatch'),
        icon: 'error',
        confirmButtonText: t('common.buttons.ok')
      });
      return;
    }

    if (newPassword.length < 6) {
      Swal.fire({
        title: t('profilePage.error.error'),
        text: t('profilePage.error.passwordMustBeAtLeast6Characters'),
        icon: 'error',
        confirmButtonText: t('common.buttons.ok')
      });
      return;
    }

    try {
      setPasswordLoading(true);
      await authService.changePassword(currentPassword, newPassword);

      // Başarılı mesajı göster
      Swal.fire({
        title: t('profilePage.success.success'),
        text: t('profilePage.success.passwordChanged'),
        icon: 'success',
        confirmButtonText: t('common.buttons.ok')
      }).then(() => {
        // Kullanıcıyı logout yap ve login sayfasına yönlendir
        dispatch(logout());
        navigate(routes.login2);
      });

      // Şifre alanlarını temizle
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      handleApiError(error, t);
    } finally {
      setPasswordLoading(false);
    }
  };

  const route = all_routes;
  const [passwordVisibility, setPasswordVisibility] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
    currentPassword: false,
  });

  const togglePasswordVisibility = (field: PasswordField) => {
    setPasswordVisibility((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  const [documents, setDocuments] = useState<FileDto[]>([]);
  const [isDocumentModalVisible, setIsDocumentModalVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<FileDto | null>(null);
  const [documentTitle, setDocumentTitle] = useState('');
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState<DocumentType | null>(null);
  const [documentLoading, setDocumentLoading] = useState(false);

  // Zimmet verilerini çek (admin olmayan kullanıcılar için)
  const { userAssets, loading: assetsLoading, fetchUserAssets } = useUserAssets(user?.id || 0);

  useEffect(() => {
    if (user?.id && user?.role !== RolesEnum.ADMIN) {
      fetchUserAssets();
    }
  }, [user?.id, user?.role, fetchUserAssets]);

  // Belge yükleme fonksiyonu
  const handleDocumentUpload = async (file: File | null) => {
    setDocumentFile(file);
  };

  // Yeni belge ekleme modalını aç
  const showAddDocumentModal = () => {
    setSelectedDocument(null);
    setDocumentTitle('');
    setDocumentFile(null);
    setDocumentType(null);
    setIsDocumentModalVisible(true);
  };

  // Belge düzenleme modalını aç
  const showEditDocumentModal = (document: FileDto) => {
    setSelectedDocument(document);
    setDocumentTitle(document.name);
    setDocumentType(document.documentType || null);
    setIsDocumentModalVisible(true);
  };

  const fetchDocuments = async () => {
    if (!user?.id) return;
    const documents = await fileService.getFileByUserDocuments(FileType.DOCUMENT, user?.id);
    setDocuments(documents);
  };
  useEffect(() => {
    fetchDocuments();
  }, []);

  // Belge silme fonksiyonu
  const handleDeleteDocument = async (document: FileDto) => {
    try {
      Swal.fire({
        title: t('profilePage.sure'),
        text: t('profilePage.deleteDocumentConfirmation'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: t('profilePage.buttons.yes'),
        cancelButtonText: t('profilePage.buttons.cancel')
      }).then(async (result) => {
        if (result.isConfirmed) {
          setDocumentLoading(true);
          await fileService.deleteFile(document.id);
          message.success(t('profilePage.success.documentDeleted'));
          fetchDocuments();
        }
      });
    } catch (error) {
      handleApiError(error, t);
    } finally {
      setDocumentLoading(false);
    }
  };

  // Belge kaydetme fonksiyonu
  const handleSaveDocument = async () => {
    if (!documentTitle.trim()) {
      message.error(t('profilePage.error.documentTitleRequired'));
      return;
    }

    try {
      setDocumentLoading(true);

      if (selectedDocument) {
        // Düzenleme işlemi
        if (documentFile) {
          //console.log(documentFile);

          await fileService.updateFileByUserDocuments(selectedDocument.id, FileType.DOCUMENT, documentTitle, documentFile, documentType);
          message.success(t('profilePage.success.documentUpdated'));
        } else {
          await fileService.updateFileByUserDocuments(selectedDocument.id, FileType.DOCUMENT, documentTitle, undefined, documentType);
          message.success(t('profilePage.success.documentUpdated'));
        }
      } else {
        // Yeni belge ekleme
        if (!documentFile) {
          message.error(t('profilePage.error.documentFileRequired'));
          return;
        }
        await fileService.uploadFile(documentFile, FileType.DOCUMENT, documentTitle, documentType);
        message.success(t('profilePage.success.documentAdded'));
      }

      setIsDocumentModalVisible(false);
      setDocumentTitle('');
      setDocumentFile(null);
      setDocumentType(null);
      setSelectedDocument(null);
      fetchDocuments();
    } catch (error) {
      handleApiError(error, t);
    } finally {
      setDocumentLoading(false);
    }
  };
  const pickerLocale = useMemo(() => {
    return i18n.language === 'tr' ? datePickerTr : datePickerEn;
  }, [i18n.language]);

  // Zimmet durumu renk ve metin fonksiyonları
  const getStatusColor = (status: AssetStatus) => {
    switch (status) {
      case AssetStatus.IN_STOCK:
        return 'success';
      case AssetStatus.ASSIGNED:
        return 'processing';
      case AssetStatus.SCRAPPED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: AssetStatus) => {
    switch (status) {
      case AssetStatus.IN_STOCK:
        return 'Envanterde';
      case AssetStatus.ASSIGNED:
        return 'Atanmış';
      case AssetStatus.SCRAPPED:
        return 'Kullanım Dışı';
      default:
        return status;
    }
  };

  // Zimmet tablo sütunları
  const assetColumns = [
    {
      title: 'Zimmet Adı',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: Asset, b: Asset) => a.name.localeCompare(b.name),
    },
    {
      title: 'Seri Numarası',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      sorter: (a: Asset, b: Asset) => a.serialNumber.localeCompare(b.serialNumber),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: AssetStatus) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
      sorter: (a: Asset, b: Asset) => a.status.localeCompare(b.status),
    },
    {
      title: 'Zimmet Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('tr-TR'),
      sorter: (a: Asset, b: Asset) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: 'Dosyalar',
      dataIndex: 'files',
      key: 'files',
      render: (files: any[]) => (
        <div>
          {files && files.length > 0 ? (
            <span className="badge bg-info">
              <i className="ti ti-file me-1"></i>
              {files.length} dosya
            </span>
          ) : (
            <span className="text-muted">-</span>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">{t('profilePage.pageTitle')}</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={'/'}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">{t('profilePage.breadcrumb.pages')}</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {t('profilePage.breadcrumb.profile')}
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          {/* /Breadcrumb */}
          <div className="card">
            <div className="card-body">
              <div className="border-bottom mb-3 pb-3">
                <h4>{t('profilePage.pageTitle')}</h4>
              </div>
              <form onSubmit={handleSave} className="border-bottom mb-3 pb-3">
                <div className="">
                  <div className="row">
                    <div className="col-md-12">
                      <div>
                        <h6 className="mb-3">{t('profilePage.profileSection.basicInformation')}</h6>
                        <div className="d-flex align-items-center flex-wrap row-gap-3 bg-light w-100 rounded p-3 mb-4">
                          <div className="d-flex align-items-center justify-content-center avatar avatar-xxl rounded-circle border border-dashed me-2 flex-shrink-0 text-dark frames">
                            {userData?.profilePicture ? (
                              <Image
                                src={getFileUrl(userData.profilePicture)}
                                alt="Profile"
                                className="w-100 h-100 rounded-circle object-fit-cover"
                                preview={{
                                  mask: (
                                    <div className="ant-image-mask-info">
                                      <i className="ti ti-eye"></i>
                                    </div>
                                  )
                                }}
                                style={{ cursor: 'pointer' }}
                              />
                            ) : (
                              <i className="ti ti-photo text-gray-3 fs-16" />
                            )}
                          </div>
                          <div className="profile-upload">
                            <div className="mb-2">
                              <h6 className="mb-1">{t('profilePage.profileSection.profilePhoto')}</h6>
                              <p className="fs-12">
                                {t('profilePage.profileSection.recommendedSize')}
                              </p>
                            </div>
                            <div className="profile-uploader d-flex align-items-center">
                              <FileUpload
                                fileType={FileType.PROFILE_PICTURE}
                                onChange={handleProfilePhotoUpload}
                                buttonText={t('profilePage.buttons.uploadPhoto')}
                                accept=".jpg,.jpeg,.png"
                                maxCount={1}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-4">
                          <label className="form-label mb-md-0">{t('profilePage.formFields.firstName')}</label>
                        </div>
                        <div className="col-md-8">
                          <input
                            type="text"
                            value={formState.firstName}
                            className="form-control"
                            onChange={(e) => handleInputChange("firstName", e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-4">
                          <label className="form-label mb-md-0">{t('profilePage.formFields.lastName')}</label>
                        </div>
                        <div className="col-md-8">
                          <input
                            type="text"
                            value={formState.lastName}
                            className="form-control"
                            onChange={(e) => handleInputChange("lastName", e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    {/* <div className="col-md-6">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-4">
                          <label className="form-label mb-md-0">{t('profilePage.formFields.email')}</label>
                        </div>
                        <div className="col-md-8">
                          <input
                            type="text"
                            value={formState.email}
                            className="form-control"
                            onChange={(e) => handleInputChange("email", e.target.value)}
                          />
                        </div>
                      </div>
                    </div> */}
                    <div className="col-md-6">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-4">
                          <label className="form-label mb-md-0">{t('profilePage.formFields.phone')}</label>
                        </div>
                        <div className="col-md-8">
                          <input
                            type="text"
                            value={formState.phone}
                            className="form-control"
                            onChange={(e) => handleInputChange("phone", e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-4">
                          <label className="form-label mb-md-0">{t('profilePage.formFields.birthDate')}</label>
                        </div>
                        <div className="col-md-8">
                          <DatePicker
                            value={formState.birthDate ? dayjs(formState.birthDate) : null}
                            onChange={(date) => {
                              if (date) {
                                // Tarihi yyyy-mm-dd formatında string olarak gönder
                                const dateString = date.format('YYYY-MM-DD');
                                handleInputChange("birthDate", dateString);
                              } else {
                                handleInputChange("birthDate", undefined);
                              }
                            }}
                            locale={pickerLocale}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-4">
                          <label className="form-label mb-md-0">{t('profilePage.formFields.maritalStatus')}</label>
                        </div>
                        <div className="col-md-8">
                          <select
                            value={formState.maritalStatus}
                            className="form-control"
                            onChange={(e) => handleInputChange("maritalStatus", e.target.value as "single" | "married")}
                          >
                            <option value="">{t('employeeAddModal.formFields.selectMaritalStatus')}</option>
                            <option value="single">{t('employeeAddModal.formFields.single')}</option>
                            <option value="married">{t('employeeAddModal.formFields.married')}</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-4">
                          <label className="form-label mb-md-0">{t('profilePage.formFields.educationLevel')}</label>
                        </div>
                        <div className="col-md-8">
                          <select
                            value={formState.educationLevel}
                            className="form-control"
                            onChange={(e) => handleInputChange("educationLevel", e.target.value as 'primary' | 'secondary' | 'bachelor' | 'master' | 'phd')}
                          >
                            <option value="">{t('employeeAddModal.formFields.selectEducationLevel')}</option>
                            <option value="primary">{t('employeeAddModal.formFields.educationLevelSelector.primary')}</option>
                            <option value="secondary">{t('employeeAddModal.formFields.educationLevelSelector.secondary')}</option>
                            <option value="bachelor">{t('employeeAddModal.formFields.educationLevelSelector.bachelor')}</option>
                            <option value="master">{t('employeeAddModal.formFields.educationLevelSelector.master')}</option>
                            <option value="phd">{t('employeeAddModal.formFields.educationLevelSelector.phd')}</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-4">
                          <label className="form-label mb-md-0">{t('profilePage.formFields.emergencyContact')}</label>
                        </div>
                        <div className="col-md-8">
                          <input
                            type="text"
                            value={formState.emergencyContact}
                            className="form-control"
                            onChange={(e) => handleInputChange("emergencyContact", e.target.value)}
                            placeholder={t('profilePage.formFields.emergencyContact')}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-4">
                          <label className="form-label mb-md-0">{t('profilePage.formFields.tcNo')}</label>
                        </div>
                        <div className="col-md-8">
                          <input disabled type="text" value={formState.nationalId} className="form-control" onChange={(e) => handleInputChange("nationalId", e.target.value)} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-2">
                          <label className="form-label mb-md-0">{t('profilePage.formFields.address')}</label>
                        </div>
                        <div className="col-md-10">
                          <input type="text" value={formState.city} className="form-control" onChange={(e) => handleInputChange("city", e.target.value)} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sağlık Bilgileri Bölümü */}
                <div className="mb-3">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-4">
                          <label className="form-label mb-md-0">Kan Grubu</label>
                        </div>
                        <div className="col-md-8">
                          <BloodTypeSelect
                            value={formState.bloodType}
                            onChange={(value) => handleInputChange("bloodType", value)}
                            placeholder="Kan grubu seçiniz (isteğe bağlı)"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="row align-items-center mb-3">
                        <div className="col-md-4">
                          <label className="form-label mb-md-0">Kronik Hastalıklar</label>
                        </div>
                        <div className="col-md-8">
                          <ChronicDiseasesInput
                            value={formState.chronicDiseases}
                            onChange={(value) => handleInputChange("chronicDiseases", value)}
                            placeholder="Kronik hastalık eklemek için yazın ve Enter'a basın"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="d-flex align-items-center justify-content-end">
                  <button
                    type="button"
                    className="btn btn-outline-light border me-3"
                  >
                    {t('profilePage.buttons.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? t('profilePage.buttons.saving') : t('profilePage.buttons.save')}
                  </button>
                </div>
              </form>
              <div className="border-bottom mb-3 mt-4 pt-3">
                <h6 className="mb-3">{t('profilePage.changePassword.title')}</h6>
                <div className="row">
                  <div className="col-md-4">
                    <div className="row align-items-center mb-3">
                      <div className="col-md-5">
                        <label className="form-label mb-md-0">
                          {t('profilePage.changePassword.currentPassword')}
                        </label>
                      </div>
                      <div className="col-md-7">
                        <div className="pass-group">
                          <input
                            type={
                              passwordVisibility.currentPassword
                                ? "text"
                                : "password"
                            }
                            className="pass-input form-control"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                          />
                          <span
                            className={`ti toggle-passwords ${passwordVisibility.currentPassword
                              ? "ti-eye"
                              : "ti-eye-off"
                              }`}
                            onClick={() =>
                              togglePasswordVisibility("currentPassword")
                            }
                          ></span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="row align-items-center mb-3">
                      <div className="col-md-5">
                        <label className="form-label mb-md-0">
                          {t('profilePage.changePassword.newPassword')}
                        </label>
                      </div>
                      <div className="col-md-7">
                        <div className="pass-group">
                          <input
                            type={
                              passwordVisibility.newPassword
                                ? "text"
                                : "password"
                            }
                            className="pass-input form-control"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                          />
                          <span
                            className={`ti toggle-passwords ${passwordVisibility.newPassword
                              ? "ti-eye"
                              : "ti-eye-off"
                              }`}
                            onClick={() =>
                              togglePasswordVisibility("newPassword")
                            }
                          ></span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="row align-items-center mb-3">
                      <div className="col-md-5">
                        <label className="form-label mb-md-0">
                          {t('profilePage.changePassword.confirmPassword')}
                        </label>
                      </div>
                      <div className="col-md-7">
                        <div className="pass-group">
                          <input
                            type={
                              passwordVisibility.confirmPassword
                                ? "text"
                                : "password"
                            }
                            className="pass-input form-control"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                          />
                          <span
                            className={`ti toggle-passwords ${passwordVisibility.confirmPassword
                              ? "ti-eye"
                              : "ti-eye-off"
                              }`}
                            onClick={() =>
                              togglePasswordVisibility("confirmPassword")
                            }
                          ></span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="d-flex justify-content-end mt-3 mb-3">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleChangePassword}
                        disabled={passwordLoading}
                      >
                        {passwordLoading ? t('profilePage.buttons.saving') : t('profilePage.buttons.changePassword')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Belgeler Bölümü */}
              {user?.role !== RolesEnum.ADMIN  &&  user?.role !== RolesEnum.SUPERADMIN && (

                <div className="border-bottom mb-3 mt-4 pt-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">{t('profilePage.documents.title')}</h6>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={showAddDocumentModal}
                    >
                      <i className="ti ti-plus me-1"></i>
                      {t('profilePage.buttons.addDocument')}
                    </button>
                  </div>

                  <div className="row">
                    {documents.length > 0 ? (
                      documents.map((document) => (
                        <div key={document.id} className="col-md-4 mb-3">
                          <div className="card h-100">
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <div>
                                  <h6 className="mb-0">{document.name}</h6>
                                  {document.documentType && (
                                    <span className="badge bg-primary text-white small mt-1">
                                      {DOCUMENT_TYPE_LABELS[document.documentType]}
                                    </span>
                                  )}
                                </div>
                                <div className="dropdown">
                                  <button className="btn btn-light btn-sm" type="button" data-bs-toggle="dropdown">
                                    <i className="ti ti-dots-vertical"></i>
                                  </button>
                                  <ul className="dropdown-menu">
                                    <li>
                                      <a
                                        className="dropdown-item"
                                        href={getFileUrl(document.path)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <i className="ti ti-eye me-2"></i>
                                        {t('profilePage.buttons.view')}
                                      </a>
                                    </li>
                                    <li>
                                      <button
                                        className="dropdown-item"
                                        onClick={() => {
                                          showEditDocumentModal(document);
                                          setSelectedDocument(document);
                                        }}
                                      >
                                        <i className="ti ti-pencil me-2"></i>
                                        {t('profilePage.buttons.edit')}
                                      </button>
                                    </li>
                                    <li>
                                      <button
                                        className="dropdown-item text-danger"
                                        onClick={() => handleDeleteDocument(document)}
                                      >
                                        <i className="ti ti-trash me-2"></i>
                                        {t('profilePage.buttons.delete')}
                                      </button>
                                    </li>
                                  </ul>
                                </div>
                              </div>
                              <p className="text-muted small mb-0">
                                {t('profilePage.documents.uploadDate')}: {new Date(document.createdAt).toLocaleDateString('tr-TR')}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="col-12">
                        <div className="text-center py-4">
                          <i className="ti ti-file-off fs-2 text-muted mb-2"></i>
                          <p className="text-muted">{t('profilePage.documents.noDocuments')}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Zimmet Bölümü */}
              {user?.role !== RolesEnum.ADMIN && user?.role !== RolesEnum.SUPERADMIN && (
                <div className="border-bottom mb-3 mt-4 pt-3">
                  <h6 className="mb-3">Zimmet Bilgileri</h6>

                  {/* Zimmet İstatistikleri */}
                  <div className="row mb-3">
                    <div className="col-md-3">
                      <div className="card border-0 shadow-sm">
                        <div className="card-body text-center p-3">
                          <div className="d-flex align-items-center justify-content-center mb-2">
                            <span className="avatar avatar-xs bg-primary me-2">
                              <i className="ti ti-archive text-white"></i>
                            </span>
                            <h4 className="mb-0">{userAssets?.length || 0}</h4>
                          </div>
                          <p className="text-muted mb-0 small">Toplam</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card border-0 shadow-sm">
                        <div className="card-body text-center p-3">
                          <div className="d-flex align-items-center justify-content-center mb-2">
                            <span className="avatar avatar-xs bg-warning me-2">
                              <i className="ti ti-user-check text-white"></i>
                            </span>
                            <h4 className="mb-0">{userAssets?.filter(asset => asset.status === AssetStatus.ASSIGNED).length || 0}</h4>
                          </div>
                          <p className="text-muted mb-0 small">Atanmış</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card border-0 shadow-sm">
                        <div className="card-body text-center p-3">
                          <div className="d-flex align-items-center justify-content-center mb-2">
                            <span className="avatar avatar-xs bg-success me-2">
                              <i className="ti ti-package text-white"></i>
                            </span>
                            <h4 className="mb-0">{userAssets?.filter(asset => asset.status === AssetStatus.IN_STOCK).length || 0}</h4>
                          </div>
                          <p className="text-muted mb-0 small">Envanterde</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card border-0 shadow-sm">
                        <div className="card-body text-center p-3">
                          <div className="d-flex align-items-center justify-content-center mb-2">
                            <span className="avatar avatar-xs bg-danger me-2">
                              <i className="ti ti-trash text-white"></i>
                            </span>
                            <h4 className="mb-0">{userAssets?.filter(asset => asset.status === AssetStatus.SCRAPPED).length || 0}</h4>
                          </div>
                          <p className="text-muted mb-0 small">Kullanım Dışı</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Zimmet Tablosu */}
                  <Table
                    columns={assetColumns}
                    dataSource={userAssets || []}
                    loading={assetsLoading}
                    rowKey="id"
                    pagination={{
                      pageSize: 10,
                      showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`,
                    }}
                    size="middle"
                    scroll={{ x: true }}
                  />
                </div>
              )}

              {/* Belge Ekleme/Düzenleme Modalı */}
              <Modal
                title={selectedDocument ? t('profilePage.documents.editDocument') : t('profilePage.documents.addDocument')}
                open={isDocumentModalVisible}
                onCancel={() => setIsDocumentModalVisible(false)}
                footer={[
                  <button
                    key="cancel"
                    type="button"
                    className="btn btn-light"
                    onClick={() => setIsDocumentModalVisible(false)}
                    disabled={documentLoading}
                  >
                    {t('profilePage.buttons.cancel')}
                  </button>,
                  <button
                    key="submit"
                    type="button"
                    className="btn btn-primary ms-2"
                    onClick={handleSaveDocument}
                    disabled={documentLoading}
                  >
                    {documentLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" />
                        {t('profilePage.buttons.saving')}
                      </>
                    ) : t('profilePage.buttons.save')}
                  </button>
                ]}
              >
                <div className="mb-3">
                  <label className="form-label">Belge Tipi</label>
                  <Select
                    placeholder="Belge tipini seçin"
                    value={documentType}
                    onChange={setDocumentType}
                    options={getDocumentTypeOptions().filter(option => {
                      // Askerlik durum belgesini sadece erkeklerde göster
                      if (option.value === 'military_service_certificate') {
                        return userData?.gender === 'male';
                      }
                      return true;
                    })}
                    style={{ width: '100%' }}
                    allowClear
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">{t('profilePage.documents.documentTitle')}</label>
                  <Input
                    placeholder={t('profilePage.documents.documentTitlePlaceholder')}
                    value={documentTitle}
                    onChange={(e) => setDocumentTitle(e.target.value)}
                  />
                </div>

                <div>
                  <label className="form-label">{t('profilePage.documents.document')}</label>
                  <FileUpload
                    fileType={FileType.DOCUMENT}
                    onChange={handleDocumentUpload}
                    maxCount={1}
                    buttonText={selectedDocument ? "Değiştir" : t('profilePage.buttons.uploadDocument')}
                    accept=".pdf,.jpg,.jpeg,.png"
                    disabled={documentLoading}
                  />
                  {selectedDocument && !documentFile && (
                    <div className="mt-2">
                      <small className="text-muted">
                        {t('profilePage.documents.currentDocument')} {selectedDocument.name + " (" + selectedDocument.path.split('/').pop() + ")"}
                      </small>
                    </div>
                  )}
                </div>
              </Modal>
            </div>
          </div>

        </div>

        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <FooterSection />
        </div>
      </div>
      {/* /Page Wrapper */}

    </>
  );
};

export default Profile;
